import { useMemo, useState, useEffect } from 'react'
import type { Balance, Contact } from '@botho/core'
import { formatBTH, parseBTH, isValidAddress } from '@botho/core'
import { Button, Input, ModalOverlay } from '@botho/ui'
import { motion } from 'motion/react'
import { AlertCircle, ChevronDown, ChevronUp, Loader2, Send, ShieldAlert, User, X, Zap } from 'lucide-react'

export type SendPrivacyLevel = 'standard' | 'private'

/**
 * Result of a fee estimate: the fee amount plus the node-computed cluster fee
 * factor display string (#635). Structurally identical to `@botho/adapters`'
 * `FeeEstimate` — kept as a local type so this UI package stays decoupled from
 * the node-adapter package (and reusable by the mobile app). "1.00x" means the
 * base rate (no cluster-wealth premium).
 */
export interface FeeEstimate {
  fee: bigint
  clusterFactorDisplay: string
}

export interface SendFormData {
  recipient: string
  amount: bigint
  privacyLevel: SendPrivacyLevel
  memo?: string
  /** Custom fee override (if not provided, uses estimated fee) */
  customFee?: bigint
}

export interface SendResult {
  success: boolean
  txHash?: string
  error?: string
}

export interface SendModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Close handler */
  onClose: () => void
  /** Current balance (for max button) */
  balance: Balance | null
  /**
   * Fee estimator function. Returns both the fee and the node-computed cluster
   * fee factor display string so the modal can show *why* the fee is what it is
   * (#635).
   */
  estimateFee: (amount: bigint, privacyLevel: SendPrivacyLevel) => Promise<FeeEstimate>
  /** Send handler */
  onSend: (data: SendFormData) => Promise<SendResult>
  /** Whether a send is in progress */
  isSending?: boolean
  /**
   * Saved address-book contacts, used to power the recipient picker and to show
   * a known contact's name inline. Optional for back-compat — when omitted, the
   * recipient field behaves as a plain text input.
   */
  contacts?: Contact[]
  /**
   * Optional search callback for contacts. When provided it's used instead of
   * filtering `contacts` locally (e.g. to delegate to the address-book search).
   */
  onSearchContacts?: (query: string) => Contact[]
  /**
   * The user's own wallet address. When provided, the modal warns (and blocks)
   * if the recipient is the user's own address. Optional for back-compat.
   */
  ownAddress?: string | null
  /**
   * Optional recipient address validator. Defaults to `@botho/core`'s
   * `isValidAddress`. Exposed as a prop mainly for testing / customisation;
   * existing callers don't need to provide it.
   */
  validateAddress?: (address: string) => boolean
}

/** Truncate an address for compact display in the picker. */
function shortAddress(address: string, len = 8): string {
  if (address.length <= len * 2 + 3) return address
  return `${address.slice(0, len)}…${address.slice(-len)}`
}

/**
 * Modal for sending BTH transactions.
 */
export function SendModal({
  isOpen,
  onClose,
  balance,
  estimateFee,
  onSend,
  isSending = false,
  contacts,
  onSearchContacts,
  ownAddress,
  validateAddress = isValidAddress,
}: SendModalProps) {
  const [recipient, setRecipient] = useState('')
  const [amount, setAmount] = useState('')
  const [privacyLevel] = useState<SendPrivacyLevel>('standard')
  const [memo, setMemo] = useState('')
  const [fee, setFee] = useState<bigint>(BigInt(0))
  // Node-computed cluster fee factor display string (#635). "1.00x" is the base
  // rate; anything above it means the sender's cluster wealth triggers Botho's
  // progressive fee.
  const [clusterFactorDisplay, setClusterFactorDisplay] = useState<string>('1.00x')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [useCustomFee, setUseCustomFee] = useState(false)
  const [customFeeInput, setCustomFeeInput] = useState('')
  const [showPicker, setShowPicker] = useState(false)

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRecipient('')
      setAmount('')
      setMemo('')
      setError(null)
      setSuccess(null)
      setShowAdvanced(false)
      setUseCustomFee(false)
      setCustomFeeInput('')
      setShowPicker(false)
      setClusterFactorDisplay('1.00x')
    }
  }, [isOpen])

  // Contact picker: matches for the current recipient query. Prefer the
  // injected search callback; otherwise filter the provided contacts locally.
  const matches = useMemo<Contact[]>(() => {
    const all = contacts ?? []
    if (all.length === 0 && !onSearchContacts) return []
    const q = recipient.trim().toLowerCase()
    if (!q) return all
    if (onSearchContacts) return onSearchContacts(q)
    return all.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.address.toLowerCase().includes(q),
    )
  }, [contacts, onSearchContacts, recipient])

  // If the entered recipient exactly matches a known contact, surface its name.
  const matchedContact = useMemo<Contact | null>(() => {
    const r = recipient.trim().toLowerCase()
    if (!r) return null
    return (contacts ?? []).find((c) => c.address.toLowerCase() === r) ?? null
  }, [contacts, recipient])

  const hasContacts = (contacts?.length ?? 0) > 0 || !!onSearchContacts
  const showPickerList = showPicker && hasContacts && matches.length > 0

  // Recipient address validation. We only surface an inline error once the user
  // has typed something — an empty field just keeps the submit button disabled.
  const trimmedRecipient = recipient.trim()
  const recipientValid = trimmedRecipient.length > 0 && validateAddress(trimmedRecipient)
  const recipientInvalid = trimmedRecipient.length > 0 && !recipientValid
  // Self-send guard: block sending to one's own address (sends are irreversible
  // and a self-send is almost always a mistake/confusion).
  const isSelfSend =
    recipientValid &&
    ownAddress != null &&
    trimmedRecipient.toLowerCase() === ownAddress.trim().toLowerCase()

  // Estimate fee when amount or privacy changes. The estimate carries the
  // node's cluster factor display string, which updates the factor row live as
  // the user types (#635).
  useEffect(() => {
    if (amount) {
      try {
        const amountBigInt = parseBTH(amount)
        estimateFee(amountBigInt, privacyLevel).then((estimate) => {
          setFee(estimate.fee)
          setClusterFactorDisplay(estimate.clusterFactorDisplay)
        })
      } catch {
        // Invalid amount
      }
    }
  }, [amount, privacyLevel, estimateFee])

  // Compute effective fee (custom or estimated)
  const effectiveFee = useCustomFee && customFeeInput
    ? (() => {
        try {
          return parseBTH(customFeeInput)
        } catch {
          return fee
        }
      })()
    : fee

  const handleSend = async () => {
    setError(null)
    setSuccess(null)

    if (!trimmedRecipient) {
      setError('Please enter a recipient address')
      return
    }

    if (!recipientValid) {
      setError('Invalid Botho address')
      return
    }

    if (isSelfSend) {
      setError('This is your own address — you can’t send to yourself')
      return
    }

    if (!amount) {
      setError('Please enter an amount')
      return
    }

    try {
      const amountBigInt = parseBTH(amount)
      const total = amountBigInt + effectiveFee

      if (balance && total > balance.available) {
        setError('Insufficient balance')
        return
      }

      const result = await onSend({
        recipient,
        amount: amountBigInt,
        privacyLevel,
        memo: memo || undefined,
        customFee: useCustomFee ? effectiveFee : undefined,
      })

      if (result.success) {
        setSuccess(`Transaction sent! Hash: ${result.txHash}`)
        setTimeout(() => {
          onClose()
        }, 2000)
      } else {
        setError(result.error || 'Transaction failed')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount')
    }
  }

  if (!isOpen) return null

  return (
    // Shared dismissal policy (#655): backdrop click / Escape close the modal,
    // suppressed while a send is in flight so an in-progress transaction can't
    // be orphaned by an accidental dismissal.
    <ModalOverlay
      onDismiss={onClose}
      dismissable={!isSending}
      className="flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-md rounded-2xl border border-[--color-steel] bg-[--color-abyss] p-6 shadow-2xl"
      >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-lg p-1 text-[--color-dim] transition-colors hover:bg-[--color-steel] hover:text-[--color-light]"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[--color-pulse]/20">
              <Send className="h-5 w-5 text-[--color-pulse]" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-[--color-light]">Send BTH</h2>
              <p className="text-sm text-[--color-dim]">Transfer funds securely</p>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Recipient */}
            <div className="relative">
              <label className="mb-1.5 block text-sm font-medium text-[--color-ghost]">
                Recipient Address
              </label>
              <Input
                placeholder="tbotho://1/... or search contacts"
                value={recipient}
                onChange={(e) => {
                  setRecipient(e.target.value)
                  setShowPicker(true)
                }}
                onFocus={() => setShowPicker(true)}
                className="font-mono text-sm"
              />
              {/* Inline contact name when the recipient matches a saved contact */}
              {matchedContact && matchedContact.name.trim() && (
                <p className="mt-1 flex items-center gap-1 text-xs text-[--color-pulse]">
                  <User className="h-3 w-3" />
                  {matchedContact.name}
                </p>
              )}
              {/* Inline validation error for a malformed address */}
              {recipientInvalid && (
                <p className="mt-1 flex items-center gap-1 text-xs text-[--color-danger]">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  Invalid Botho address
                </p>
              )}
              {/* Self-send warning */}
              {isSelfSend && (
                <p className="mt-1 flex items-center gap-1 text-xs text-[--color-danger]">
                  <ShieldAlert className="h-3 w-3 shrink-0" />
                  This is your own address — you can’t send to yourself.
                </p>
              )}
              {/* Searchable contact picker */}
              {showPickerList && (
                <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-lg border border-[--color-steel] bg-[--color-abyss] shadow-xl">
                  {matches.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setRecipient(c.address)
                        setShowPicker(false)
                      }}
                      className="flex w-full flex-col items-start gap-0.5 border-b border-[--color-steel]/50 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-[--color-steel]/40"
                    >
                      <span className="text-sm text-[--color-light]">
                        {c.name.trim() || 'Unnamed contact'}
                      </span>
                      <span className="font-mono text-xs text-[--color-dim]">
                        {shortAddress(c.address)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Amount */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-sm font-medium text-[--color-ghost]">
                  Amount
                </label>
                {balance && (
                  <button
                    onClick={() => setAmount(formatBTH(balance.available, { separators: false }))}
                    className="text-xs text-[--color-pulse] hover:underline"
                  >
                    Max: {formatBTH(balance.available)} BTH
                  </button>
                )}
              </div>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pr-16 font-mono"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-[--color-dim]">
                  BTH
                </span>
              </div>
            </div>

            {/* Memo */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[--color-ghost]">
                Memo <span className="text-[--color-dim]">(optional)</span>
              </label>
              <Input
                placeholder="Add a note..."
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </div>

            {/* Advanced Options */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex w-full items-center justify-between rounded-lg border border-[--color-steel] bg-[--color-slate]/50 px-3 py-2 text-sm text-[--color-ghost] transition-colors hover:border-[--color-pulse]/50"
              >
                <span>Advanced Options</span>
                {showAdvanced ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>

              {showAdvanced && (
                <div className="mt-2 space-y-3 rounded-lg border border-[--color-steel] bg-[--color-slate]/30 p-3">
                  {/* Custom Fee Toggle */}
                  <label className="flex cursor-pointer items-center justify-between">
                    <div>
                      <span className="text-sm text-[--color-ghost]">Custom Fee</span>
                      <p className="text-xs text-[--color-dim]">Override the estimated network fee</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setUseCustomFee(!useCustomFee)}
                      className={`relative h-6 w-11 rounded-full transition-colors ${
                        useCustomFee ? 'bg-[--color-pulse]' : 'bg-[--color-steel]'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                          useCustomFee ? 'left-[22px]' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </label>

                  {useCustomFee && (
                    <div>
                      <div className="relative">
                        <Input
                          type="text"
                          placeholder="0.00"
                          value={customFeeInput}
                          onChange={(e) => setCustomFeeInput(e.target.value)}
                          className="pr-16 font-mono"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-[--color-dim]">
                          BTH
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-[--color-dim]">
                        Estimated fee: {formatBTH(fee)} BTH
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Fee Summary */}
            <div className="rounded-lg border border-[--color-steel] bg-[--color-slate]/50 p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[--color-ghost]">
                  Network Fee{useCustomFee && ' (custom)'}
                </span>
                <span className="font-mono text-[--color-light]">{formatBTH(effectiveFee)} BTH</span>
              </div>
              {/*
                Cluster fee factor (#635). Above the 1.00x base rate the sender's
                cluster wealth triggers Botho's progressive fee, so we surface the
                multiplier with a one-line explanation. At the base rate we show
                reassuring copy so a first-time user isn't confused by a factor
                they didn't expect. The node computes the display string — no
                client-side factor table.
              */}
              {clusterFactorDisplay !== '1.00x' ? (
                <div className="mt-2 border-t border-[--color-steel] pt-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-[--color-ghost]">
                      <AlertCircle className="h-3 w-3 shrink-0 text-[--color-warning]" />
                      Progressive rate
                    </span>
                    <span className="font-mono font-semibold text-[--color-warning]">
                      {clusterFactorDisplay}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-[--color-dim]">
                    Your cluster&rsquo;s total holdings trigger Botho&rsquo;s progressive fee —
                    larger clusters pay proportionally more for network resources.
                  </p>
                </div>
              ) : (
                <p className="mt-1 text-xs text-[--color-dim]">
                  Standard rate — no cluster-wealth premium
                </p>
              )}
              {amount && (
                <div className="mt-2 flex items-center justify-between border-t border-[--color-steel] pt-2 text-sm">
                  <span className="font-medium text-[--color-ghost]">Total</span>
                  <span className="font-mono font-semibold text-[--color-pulse]">
                    {formatBTH(parseBTH(amount || '0') + effectiveFee)} BTH
                  </span>
                </div>
              )}
            </div>

            {/* Error/Success */}
            {error && (
              <div className="rounded-lg border border-[--color-danger]/30 bg-[--color-danger]/10 p-3 text-sm text-[--color-danger]">
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-lg border border-[--color-success]/30 bg-[--color-success]/10 p-3 text-sm text-[--color-success]">
                {success}
              </div>
            )}

            {/* Submit */}
            <Button
              onClick={handleSend}
              disabled={isSending || !recipientValid || isSelfSend || !amount}
              className="w-full"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Send Transaction
                </>
              )}
            </Button>
          </div>
      </motion.div>
    </ModalOverlay>
  )
}
